import { UserInfo } from '@amzn/awscat-portal-authentication-library';
import { AdjustmentsService, PortfolioService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Portfolio } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, FlashContextInterface, FlashType, withAuthContext, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Alert, Box, Button, ButtonDropdown, ButtonDropdownProps, Checkbox,
    Input, NonCancelableCustomEvent, SpaceBetween } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions, RecentPortfoliosActions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import Pluralize from '../../common/Pluralize';
import AWSCATTable from '../../common/table/Table-index';
import { CustomPropertyFilterGroup } from '../../common/table/types';
import { CAT_PORTAL_PREFIX, CATDEV_PROVIDER, getAuthProvider } from '../../common/util';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { NavigationContext } from '../../contexts/NavigationContext';
import { RecentPortfoliosContext } from '../../contexts/RecentPortfoliosContext';
import ConfirmationModal from '../common/ConfirmationModal';
import { InfoLink } from '../common/InfoLink';
import { PortfolioAction, PortfolioViewModel } from './interface';
import { columnDefinitions, getTagColumnDefinitions } from './List-helper';
import SharePortfolioModal from './SharePortfolio';

type PortfolioListProps = FlashContextInterface & AuthContextInterface;

const portfolioService = new PortfolioService();
const adjustmentsService = new AdjustmentsService();

const PortfolioList: FunctionComponent<PortfolioListProps> = ({flash, auth}): JSX.Element => {

    const convertPortfolios = async (portfolios: Portfolio[]): Promise<PortfolioViewModel[]> => {
        const authProvider = getAuthProvider();
        const userPrefix = authProvider === CATDEV_PROVIDER ? '' : CAT_PORTAL_PREFIX;
        // get the unique ids of the updaters
        const updaterUsersIds = [...new Set(portfolios.map(portfolio => portfolio?.updatedBy?.replace(userPrefix, '') ?? '').filter(item => item))];
        let updaterUsersInfo: UserInfo[] = [];
        // get the email address of the unique updaters
        updaterUsersInfo = await auth.getUserInfoList(updaterUsersIds);

        return portfolios.map(portfolio => {
            const updatedByEmail = updaterUsersInfo?.find(user => userPrefix + user.userId === portfolio.updatedBy)?.email || '-';
            return {
                portfolioId: portfolio.id,
                portfolioName: portfolio.name,
                accountName: portfolio.accountName ?? '',
                createdAt: portfolio.createdAt?.toString(),
                updatedAt: portfolio.updatedAt?.toString(),
                updatedBy: updatedByEmail,
                isDemo: portfolio.isTestPortfolio,
                permittedActions: portfolio.permittedActions,
                tags: portfolio.tags
            };
        });
    };

    const { panelHandler } = useContext(HelpPanelContext);
    const { setNavigationClosed } = useContext(NavigationContext);
    const [ portfolioItems, setPortfolioItems ] = useState<PortfolioViewModel[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ selectedPortfolios, setSelectedPortfolios ] = useState<PortfolioViewModel[]>([]);   

    const { visited } = useContext(RecentPortfoliosContext);
    
    const isMounted = useIsMounted();
    const listPortfolios = async () => {
        setLoading(true);
        try {
            const portfolios: Portfolio[] = await portfolioService.listPortfolios();
            const convertedPortfolios = await convertPortfolios(portfolios);
            if (isMounted()) {
                setPortfolioItems(convertedPortfolios);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_PORTFOLIOS);
                setPortfolioItems([]);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.ALL_PORTFOLIOS);
    }, [ panelHandler ]);

    useEffect(() => {
        setNavigationClosed();
        
        listPortfolios();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const tableInfo = (
        <InfoLink topic={HelpPanelTopicOptions.DEFAULT} />
    );

    const history = useHistory();
    const moveToCreatePortfolio = () => {
        history.push('/new-portfolio');
    };
    const moveToEditPortfolioProperties = (portfolioId: string) => {
        history.push({ pathname: `/portfolio/${portfolioId}/properties`});
    };

    const [deleteVisible, setDeleteVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [copyVisible, setCopyVisible] = useState(false);
    const [shareVisible, setShareVisible] = useState(false);
    const [portfolioToEdit, setPortfolioToEdit] = useState({
        name: '',
        isTestPortfolio: false,
        id: '',
    });
    const [portfolioToCopy, setPortfolioToCopy] = useState({
        name: '',
        portfolioId: '',
    });

    const hasPermission = (portfolio: PortfolioViewModel, action: PortfolioAction) =>
        portfolio?.permittedActions?.find(permittedAction => permittedAction === action);

    const isSharingAllowed = (): boolean => selectedPortfolios.every(portfolio => hasPermission(portfolio, PortfolioAction.ALL));
    const isEditAllowed = (): boolean => selectedPortfolios.every(portfolio =>
        hasPermission(portfolio, PortfolioAction.ALL) || hasPermission(portfolio, PortfolioAction.UPDATE));
    const isDeleteAllowed = (): boolean => selectedPortfolios.every(portfolio => hasPermission(portfolio, PortfolioAction.ALL));
    const isCopyAllowed = (): boolean => selectedPortfolios.every(portfolio =>
        hasPermission(portfolio, PortfolioAction.ALL) || hasPermission(portfolio, PortfolioAction.VIEW));

    const onConfirmDeletion = async () => {
        setLoading(true);
        try {
            await Promise.all(selectedPortfolios.map(async selectedPortfolio => {
                visited(selectedPortfolio.portfolioId, RecentPortfoliosActions.REMOVE);
                await adjustmentsService.deleteAdjustmentsByPortfolioId(selectedPortfolio.portfolioId);
                return await portfolioService.delete(selectedPortfolio.portfolioId);
            }));
            await listPortfolios();
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.FAIL_TO_DELETE_PORTFOLIO);
        }
        setLoading(false);
        setDeleteVisible(false);
    };

    const onConfirmEdit = async () => {
        setLoading(true);
        try {
            await portfolioService.update(portfolioToEdit);
            await listPortfolios();
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.FAIL_TO_UPDATE_PORTFOLIO);
        }
        setLoading(false);
        setEditVisible(false);
    };

    const onConfirmCopy = async () => {
        setLoading(true);
        try {
            await portfolioService.copy(portfolioToCopy.portfolioId, portfolioToCopy.name);
            await listPortfolios();
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.FAIL_TO_COPY_PORTFOLIO);
        }
        setLoading(false);
        setCopyVisible(false);
    };

    const showCopyModal = () => {
        if (selectedPortfolios.length !== 1) {
            return;
        }
        setPortfolioToCopy({
            portfolioId: selectedPortfolios[0].portfolioId,
            name: `${selectedPortfolios[0].portfolioName}-copy`,
        });
        setCopyVisible(true);
    };

    const getActionItems = (): ButtonDropdownProps.ItemOrGroup[] => {
        return [
            { text: constants.EDIT_PROPERTIES_BUTTON_TEXT, id: 'edit', disabled: selectedPortfolios.length !== 1 || !isEditAllowed() },
            { text: constants.DELETE_BUTTON_TEXT, id: 'delete', disabled: !selectedPortfolios.length || !isDeleteAllowed() },
            { text: constants.COPY_BUTTON_TEXT, id: 'copy', disabled: selectedPortfolios.length !== 1 || !isCopyAllowed() },
        ];
    };

    const onItemClick = (event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
        switch(event.detail.id) {
            case 'delete':
                setDeleteVisible(true);
                return;
            case 'edit':
                if (selectedPortfolios.length !== 1) {
                    return;
                }
                moveToEditPortfolioProperties(selectedPortfolios[0].portfolioId);
                return;
            case 'copy':
                showCopyModal();
                return;
            default:
        }
    };

    const editButtonDisabled = !portfolioToEdit?.name
        || portfolioToEdit?.name === selectedPortfolios[0]?.portfolioName && portfolioToEdit.isTestPortfolio === selectedPortfolios[0]?.isDemo;
    const copyButtonDisabled = !portfolioToCopy?.name;

    const defaultSortingState: SortingState<PortfolioViewModel> = {
        isDescending: true,
        sortingColumn: {
            sortingField: 'updatedAt',
        },
    };

    const isShareDisabled = (): boolean => {
        return selectedPortfolios.length !== 1 || !isSharingAllowed();
    };

    const customGroup: CustomPropertyFilterGroup<PortfolioViewModel> = {
        propertyDefinitions: getTagColumnDefinitions(portfolioItems),
        properties: constants.TAGS, 
        values: constants.TAG_VALUES,
        group: 'tags'
    };

    return (
        <div>
            <AWSCATTable
                trackBy='portfolioId'
                resourceName={constants.PORTFOLIOS}
                resizeable
                items={portfolioItems}
                columnDefinitions={columnDefinitions}
                loading={loading}
                headerInfo={tableInfo}
                autoGenerateVisibleContent={true}
                headerActions={
                    <SpaceBetween direction='horizontal' size='m'>
                        <Button iconName='refresh' onClick={listPortfolios} loading={loading} />
                        <Button
                            onClick={() => setShareVisible(true)}
                            variant="normal"
                            disabled={isShareDisabled()}>{constants.SHARE_BUTTON_TEXT}</Button>
                        <ButtonDropdown items={getActionItems()} variant="normal" onItemClick={onItemClick}>{constants.ACTION_BUTTON_TEXT}</ButtonDropdown>
                        <Button onClick={moveToCreatePortfolio} variant="primary">{constants.CREATE_PORTFOLIO}</Button>
                    </SpaceBetween>
                }
                onSelectionChange={setSelectedPortfolios as () => void}
                enableAutoSorting
                selectionType='multi'
                defaultSortBy={defaultSortingState}
                customPropertyFilterGroup={customGroup}
            />
            { deleteVisible ? (
                <ConfirmationModal
                    friction='delete'
                    visible
                    header={constants.DELETE_PORTFOLIOS}
                    onConfirm={onConfirmDeletion}
                    confirmButton={constants.DELETE_BUTTON_TEXT}
                    confirmLoading={loading}
                    onDismiss={() => setDeleteVisible(false)} >
                    <Alert header={constants.THIS_ACTION_CANNOT_BE_UNDONE} dismissible={false} type='error'></Alert>
                    {constants.YOU_ARE_ABOUT_TO_DELETE} {(<Pluralize singular={'portfolio'} count={selectedPortfolios.length} />)}.
                </ConfirmationModal>
            ) : null }
            { editVisible ? (
                <ConfirmationModal
                    visible
                    header={`${constants.EDIT_BUTTON_TEXT} ${selectedPortfolios[0]?.portfolioName}`}
                    onConfirm={onConfirmEdit}
                    onDismiss={() => setEditVisible(false)}
                    confirmDisabled={editButtonDisabled}
                    confirmLoading={loading}
                    confirmButton={constants.UPDATE_BUTTON_TEXT} >
                    <Box>{constants.CREATE_PORTFOLIO_NAME}</Box>
                    <Input
                        value={portfolioToEdit.name}
                        onChange={event => setPortfolioToEdit({...portfolioToEdit, name: event.detail.value})} />
                    <Box>{constants.TEST_PORTFOLIO}</Box>
                    <Checkbox
                        checked={portfolioToEdit.isTestPortfolio}
                        onChange={event => setPortfolioToEdit({...portfolioToEdit, isTestPortfolio: event.detail.checked})}>
                        {constants.TEST_PORTFOLIO_DESCRIPTION}
                    </Checkbox>
                </ConfirmationModal>
            ) : null}
            { copyVisible ? (
                <ConfirmationModal
                    visible
                    header={`${constants.COPY_BUTTON_TEXT} ${selectedPortfolios[0]?.portfolioName}`}
                    onConfirm={onConfirmCopy}
                    onDismiss={() => setCopyVisible(false)}
                    confirmDisabled={copyButtonDisabled}
                    confirmLoading={loading}
                    confirmButton={constants.COPY_BUTTON_TEXT} >
                    <Box>{constants.NEW_PORTFOLIO_NAME}</Box>
                    <Input
                        value={portfolioToCopy.name}
                        onChange={event => setPortfolioToCopy({...portfolioToCopy, name: event.detail.value})} />
                    <Box> {constants.FOLLOWING_WILL_BE_COPIED} </Box>
                    {constants.DATE_TYPES_THAT_WILL_BE_COPIED}
                </ConfirmationModal>
            ) : null}
            { shareVisible ? (
                <SharePortfolioModal selectedPortfolio={selectedPortfolios[0]} closeModal={() => setShareVisible(false)}></SharePortfolioModal>
            ) : null}
        </div>
    );
};

export default withAuthContext(withFlashContext(PortfolioList));
